import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, withKeys as _withKeys, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, Fragment as _Fragment, createTextVNode as _createTextVNode, renderList as _renderList, resolveComponent as _resolveComponent, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = ["src"];
const _hoisted_2 = {
    key: 0,
    class: "fieldError"
};
const _hoisted_3 = { class: "stepHeading" };
const _hoisted_4 = { class: "codeRow" };
const _hoisted_5 = { class: "codeField" };
const _hoisted_6 = ["value", "onInput", "onKeydown"];
const _hoisted_7 = { class: "passwordFields" };
const _hoisted_8 = { class: "signInField" };
const _hoisted_9 = ["src"];
const _hoisted_10 = ["src"];
const _hoisted_11 = {
    key: 0,
    class: "fieldError"
};
const _hoisted_12 = { class: "ruleList" };
const _hoisted_13 = {
    key: 0,
    class: "fieldError"
};
const _hoisted_14 = { class: "stepHeading" };
const _hoisted_15 = { class: "codeRow" };
const _hoisted_16 = { class: "codeField" };
const _hoisted_17 = ["value", "onInput", "onKeydown"];
const _hoisted_18 = {
    key: 0,
    class: "fieldError centre"
};
import { ref, computed, nextTick, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { setApiConfig } from '@/composables/useApi';
import { useAppState } from '@/composables/useAppState';
import { useCustomer } from '@/composables/useCustomer';
import { passwordRules, passwordIsValid } from '@/utils/passwordRules';
import { setCreds } from '@/utils/native';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import MGTextField from './elements/MGTextField.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'SignInForgotPasswordPage',
    setup(__props) {
        const route = useRoute();
        const router = useRouter();
        const state = useAppState();
        const { requestPasswordReset, verifyPasswordResetCode, setPassword } = useCustomer();
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        // Reached before there's a session, so the API config comes from route meta
        // rather than via useAuth() — same as SignInPage.
        const apiUrl = route.meta.api;
        const configUri = route.meta.configUri;
        state.api = apiUrl;
        state.configUri = configUri;
        setApiConfig({
            api: apiUrl,
            configUri: configUri,
            useCache: state.useCache,
            useCachedEndpoints: state.useCachedEndpoints,
            cachedBlacklist: state.cachedBlacklist,
            cookies: cookies,
            router: router,
        });
        // Steps rather than routes: App.vue keys the router-view on the query, so a query change
        // would remount this page and lose the email and the reset reference.
        const step = ref('email');
        const email = ref('');
        // Plain inputs rather than v-otp-input, as SignInPinPage does: we render the dots
        // ourselves, so masking is instant — iOS briefly reveals the last character of anything
        // the browser masks. The same boxes serve the 5 digit reset code and the 4 digit PIN.
        const digits = ref([]);
        const reveal = ref(false);
        const pinStep = ref('set');
        const pin = ref('');
        // The guid VerifyResetCodeRequests hands back, which SetRequests then needs
        const reference = ref('');
        const password = ref('');
        const confirmPassword = ref('');
        const showPassword = ref(false);
        const showConfirm = ref(false);
        const error = ref('');
        const busy = ref(false);
        const resendCooldown = ref(0);
        let resendTimer = null;
        const entered = computed(() => digits.value.join(''));
        const headerTitle = computed(() => {
            if (step.value === 'email')
                return 'Forgot your password?';
            if (step.value === 'pin')
                return 'Security';
            return 'Reset your password';
        });
        const rules = computed(() => passwordRules(password.value));
        const mismatch = computed(() => !!confirmPassword.value && confirmPassword.value !== password.value);
        const canSave = computed(() => passwordIsValid(password.value) && confirmPassword.value === password.value);
        const boxes = ref([]);
        function setBox(el, i) {
            if (el)
                boxes.value[i] = el;
        }
        // preventScroll is the whole point: without it iOS scrolls each newly focused box into
        // view, which drags the short page off-screen (same fix as SignInPinPage).
        function focusBox(i) {
            boxes.value[i]?.focus({ preventScroll: true });
        }
        // Empties the boxes to the given length and puts the caret in the first one
        function startDigits(count) {
            digits.value = Array(count).fill('');
            nextTick(() => focusBox(0));
        }
        // What a box shows: the digit itself, or a bullet once the PIN step masks it
        function boxValue(i) {
            const digit = digits.value[i] || '';
            if (step.value !== 'pin' || reveal.value)
                return digit;
            return digit ? '•' : '';
        }
        // The bound value may be unchanged (bullet to bullet, or a digit that didn't move), so
        // Vue won't repaint for us — push the model back into the DOM by hand
        function repaintBoxes() {
            digits.value.forEach((_, n) => {
                if (boxes.value[n])
                    boxes.value[n].value = boxValue(n);
            });
        }
        function onDigitInput(i, e) {
            const el = e.target;
            const typed = el.value.replace(/[^0-9]/g, '');
            error.value = '';
            // Autofill — the browser's "fill from email" prompt, or a paste — drops the whole code
            // into the one box it fired on, so spread it across this box and the ones after it
            if (typed.length > 1) {
                const spread = typed.slice(0, digits.value.length - i).split('');
                spread.forEach((d, n) => { digits.value[i + n] = d; });
                nextTick(() => {
                    repaintBoxes();
                    focusBox(Math.min(i + spread.length, digits.value.length - 1));
                });
                return;
            }
            // The box already holds a bullet when masked, so take the last digit typed
            const digit = typed.slice(-1);
            digits.value[i] = digit;
            el.value = boxValue(i);
            if (digit && i < digits.value.length - 1)
                focusBox(i + 1);
            if (step.value === 'pin' && pinStep.value === 'set' && entered.value.length === 4)
                submitPin();
        }
        function onDigitKeydown(i, e) {
            if (e.key !== 'Backspace')
                return;
            // Step back out of an already-empty box, clearing as we go
            if (!digits.value[i] && i > 0) {
                e.preventDefault();
                digits.value[i - 1] = '';
                focusBox(i - 1);
            }
        }
        function sendCode() {
            if (!email.value || busy.value)
                return;
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
                error.value = 'Please enter a valid email address';
                return;
            }
            error.value = '';
            busy.value = true;
            requestPasswordReset(email.value, () => {
                busy.value = false;
                step.value = 'code';
                startDigits(5);
            });
        }
        // Both the expired screen and the code screen's resend link land here.
        // The cooldown stops an impatient tap sending a handful of codes at once
        function resend() {
            if (busy.value || resendCooldown.value > 0)
                return;
            resendCooldown.value = 10;
            resendTimer = setInterval(() => {
                resendCooldown.value--;
                if (resendCooldown.value <= 0)
                    clearInterval(resendTimer);
            }, 1000);
            sendCode();
        }
        onUnmounted(() => clearInterval(resendTimer));
        function verifyCode() {
            if (entered.value.length < 5 || busy.value)
                return;
            busy.value = true;
            verifyPasswordResetCode(email.value, entered.value, (result) => {
                busy.value = false;
                // The endpoint doesn't distinguish a wrong code from a stale one, so both land on
                // the expired screen, whose only way on is a fresh code anyway
                if (!result) {
                    step.value = 'expired';
                    return;
                }
                reference.value = result;
                step.value = 'password';
            });
        }
        function savePassword() {
            if (!canSave.value || busy.value)
                return;
            error.value = '';
            busy.value = true;
            setPassword(reference.value, email.value, password.value, (success) => {
                busy.value = false;
                if (success) {
                    // useApi.refreshLogin replays these on a 401, so a stale pass cookie would make
                    // every session refresh fail silently from here on. Same 1m expiry as SignInPage.
                    cookies?.set('user', email.value, '1m');
                    cookies?.set('pass', password.value, '1m');
                    pinStep.value = 'set';
                    pin.value = '';
                    reveal.value = false;
                    step.value = 'pin';
                    startDigits(4);
                    return;
                }
                error.value = "We couldn't save that password. Please try again.";
            });
        }
        function submitPin() {
            if (!/^\d{4}$/.test(entered.value)) {
                error.value = 'PIN must be a 4 digit number';
                return;
            }
            if (pinStep.value === 'set') {
                pin.value = entered.value;
                pinStep.value = 'confirm';
                startDigits(4);
                return;
            }
            if (entered.value !== pin.value) {
                // Back to the first entry, not a retry of the confirm: otherwise a mistyped first
                // PIN can never be corrected, since there's no back button on this step
                error.value = 'PINs do not match. Please set it again.';
                pinStep.value = 'set';
                pin.value = '';
                startDigits(4);
                return;
            }
            // Hand the PIN and the new credentials to the native host, as SignInPinPage does.
            // On web the channel isn't there and this no-ops.
            setCreds(pin.value, email.value, password.value);
            step.value = 'done';
        }
        function back() {
            if (step.value === 'password') {
                step.value = 'code';
                return;
            }
            if (step.value === 'code') {
                step.value = 'email';
                return;
            }
            router.replace({ path: '/signin' });
        }
        onMounted(() => {
            // SignInPage stashes whatever was typed there so it needn't be retyped. sessionStorage
            // rather than a query param: App.vue keys the router-view on the query, and the address
            // has no business being in the URL.
            const stashed = sessionStorage.getItem('signinEmail');
            if (stashed) {
                email.value = stashed;
                sessionStorage.removeItem('signinEmail');
            }
        });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (step.value !== 'expired' && step.value !== 'done')
                    ? (_openBlock(), _createBlock(HeaderBlock, {
                        key: 0,
                        title: headerTitle.value,
                        titleSize: 20,
                        border: 0,
                        backButton: step.value !== 'pin',
                        onBack: back
                    }, null, 8, ["title", "backButton"]))
                    : _createCommentVNode("", true),
                _createElementVNode("div", {
                    class: _normalizeClass(["forgotPage", { centred: step.value === 'expired' || step.value === 'done' }])
                }, [
                    (step.value === 'email')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _cache[13] || (_cache[13] = _createElementVNode("p", { class: "forgotSubtitle" }, "Enter the email you use for Money Guided and we'll send you a secure reset code", -1)),
                            _createElementVNode("div", {
                                class: _normalizeClass(["signInField", { hasError: error.value }])
                            }, [
                                _cache[12] || (_cache[12] = _createElementVNode("label", { for: "forgotEmail" }, "Email address", -1)),
                                _createVNode(MGTextField, {
                                    id: "forgotEmail",
                                    modelValue: email.value,
                                    "onUpdate:modelValue": [
                                        _cache[1] || (_cache[1] = ($event) => ((email).value = $event)),
                                        _cache[2] || (_cache[2] = ($event) => (error.value = ''))
                                    ],
                                    type: "email",
                                    variant: "outlined",
                                    density: "compact",
                                    "hide-details": "",
                                    "single-line": "",
                                    autocomplete: "email",
                                    placeholder: "you@example.com",
                                    "bg-color": "white",
                                    onKeyup: _withKeys(sendCode, ["enter"])
                                }, {
                                    "append-inner": _withCtx(() => [
                                        (email.value)
                                            ? (_openBlock(), _createElementBlock("img", {
                                                key: 0,
                                                class: "fieldIcon",
                                                src: require('@/assets/signin/field-clear-20px.svg'),
                                                alt: "Clear",
                                                onClick: _cache[0] || (_cache[0] = ($event) => { email.value = ''; error.value = ''; })
                                            }, null, 8, _hoisted_1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _: 1
                                }, 8, ["modelValue"]),
                                (error.value)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(error.value), 1))
                                    : _createCommentVNode("", true)
                            ], 2),
                            _createVNode(MGButton, {
                                class: "ctaButton",
                                text: "Send reset code",
                                arrow: true,
                                fullWidth: true,
                                noCaps: true,
                                disabled: !email.value || busy.value,
                                onClick: sendCode
                            }, null, 8, ["disabled"]),
                            _createVNode(MGButton, {
                                class: "linkCta",
                                text: "Back to sign in",
                                noCaps: true,
                                stacked: true,
                                fullWidth: false,
                                transparent: true,
                                arrow: true,
                                inlineArrow: true,
                                onClick: _cache[3] || (_cache[3] = ($event) => (_unref(router).replace({ path: '/signin' })))
                            })
                        ], 64))
                        : (step.value === 'code')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createElementVNode("div", _hoisted_3, [
                                    _cache[16] || (_cache[16] = _createElementVNode("h1", null, "Check your inbox", -1)),
                                    _createElementVNode("p", null, [
                                        _cache[14] || (_cache[14] = _createTextVNode("If ")),
                                        _createElementVNode("strong", null, _toDisplayString(email.value), 1),
                                        _cache[15] || (_cache[15] = _createTextVNode(" is linked to a Money Guided account, a 5 digit code is on its way."))
                                    ])
                                ]),
                                _createElementVNode("div", _hoisted_4, [
                                    _createElementVNode("div", _hoisted_5, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(digits.value, (_, i) => {
                                            return (_openBlock(), _createElementBlock("input", {
                                                key: i,
                                                ref_for: true,
                                                ref: (el) => setBox(el, i),
                                                class: "codeBox",
                                                value: boxValue(i),
                                                type: "text",
                                                inputmode: "numeric",
                                                autocomplete: "one-time-code",
                                                "aria-label": "Reset code digit",
                                                onInput: ($event) => (onDigitInput(i, $event)),
                                                onKeydown: ($event) => (onDigitKeydown(i, $event))
                                            }, null, 40, _hoisted_6));
                                        }), 128))
                                    ])
                                ]),
                                _createVNode(MGButton, {
                                    class: "ctaButton",
                                    text: "Continue",
                                    arrow: true,
                                    fullWidth: true,
                                    noCaps: true,
                                    disabled: entered.value.length < 5 || busy.value,
                                    onClick: verifyCode
                                }, null, 8, ["disabled"]),
                                _cache[17] || (_cache[17] = _createElementVNode("p", { class: "forgotHint" }, "Give it a few minutes to arrive, and check your spam or junk folder too.", -1)),
                                _createVNode(MGButton, {
                                    class: _normalizeClass(["linkCta", { cooling: resendCooldown.value > 0 }]),
                                    text: "Resend email",
                                    noCaps: true,
                                    stacked: true,
                                    fullWidth: false,
                                    transparent: true,
                                    arrow: true,
                                    inlineArrow: true,
                                    disabled: busy.value,
                                    onClick: resend
                                }, null, 8, ["class", "disabled"])
                            ], 64))
                            : (step.value === 'expired')
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                    _cache[18] || (_cache[18] = _createElementVNode("div", { class: "stepHeading" }, [
                                        _createElementVNode("h1", null, "That code has expired"),
                                        _createElementVNode("p", null, "Reset codes only work for a limited time. We can send you a fresh one.")
                                    ], -1)),
                                    _createVNode(MGButton, {
                                        class: "ctaButton",
                                        text: "Send a new code",
                                        arrow: true,
                                        fullWidth: true,
                                        noCaps: true,
                                        disabled: busy.value,
                                        onClick: resend
                                    }, null, 8, ["disabled"])
                                ], 64))
                                : (step.value === 'password')
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                        _cache[23] || (_cache[23] = _createElementVNode("div", { class: "forgotHeading" }, [
                                            _createElementVNode("h1", null, "Create a new password"),
                                            _createElementVNode("p", null, "Enter your password")
                                        ], -1)),
                                        _createElementVNode("div", _hoisted_7, [
                                            _createElementVNode("div", _hoisted_8, [
                                                _cache[19] || (_cache[19] = _createElementVNode("label", { for: "newPassword" }, "Password", -1)),
                                                _createVNode(MGTextField, {
                                                    id: "newPassword",
                                                    modelValue: password.value,
                                                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((password).value = $event)),
                                                    type: showPassword.value ? 'text' : 'password',
                                                    variant: "outlined",
                                                    density: "compact",
                                                    "hide-details": "",
                                                    "single-line": "",
                                                    autocomplete: "new-password",
                                                    "bg-color": "white"
                                                }, {
                                                    "append-inner": _withCtx(() => [
                                                        _createVNode(_component_v_icon, {
                                                            class: "fieldIcon",
                                                            icon: showPassword.value ? 'mdi-eye' : 'mdi-eye-off',
                                                            "aria-label": showPassword.value ? 'Hide password' : 'Show password',
                                                            onClick: _cache[4] || (_cache[4] = ($event) => (showPassword.value = !showPassword.value))
                                                        }, null, 8, ["icon", "aria-label"]),
                                                        (password.value)
                                                            ? (_openBlock(), _createElementBlock("img", {
                                                                key: 0,
                                                                class: "fieldIcon",
                                                                src: require('@/assets/signin/field-clear-20px.svg'),
                                                                alt: "Clear",
                                                                onClick: _cache[5] || (_cache[5] = ($event) => (password.value = ''))
                                                            }, null, 8, _hoisted_9))
                                                            : _createCommentVNode("", true)
                                                    ]),
                                                    _: 1
                                                }, 8, ["modelValue", "type"])
                                            ]),
                                            _createElementVNode("div", {
                                                class: _normalizeClass(["signInField", { hasError: mismatch.value }])
                                            }, [
                                                _cache[20] || (_cache[20] = _createElementVNode("label", { for: "confirmPassword" }, "Confirm password", -1)),
                                                _createVNode(MGTextField, {
                                                    id: "confirmPassword",
                                                    modelValue: confirmPassword.value,
                                                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((confirmPassword).value = $event)),
                                                    type: showConfirm.value ? 'text' : 'password',
                                                    variant: "outlined",
                                                    density: "compact",
                                                    "hide-details": "",
                                                    "single-line": "",
                                                    autocomplete: "new-password",
                                                    "bg-color": "white"
                                                }, {
                                                    "append-inner": _withCtx(() => [
                                                        _createVNode(_component_v_icon, {
                                                            class: "fieldIcon",
                                                            icon: showConfirm.value ? 'mdi-eye' : 'mdi-eye-off',
                                                            "aria-label": showConfirm.value ? 'Hide password' : 'Show password',
                                                            onClick: _cache[7] || (_cache[7] = ($event) => (showConfirm.value = !showConfirm.value))
                                                        }, null, 8, ["icon", "aria-label"]),
                                                        (confirmPassword.value)
                                                            ? (_openBlock(), _createElementBlock("img", {
                                                                key: 0,
                                                                class: "fieldIcon",
                                                                src: require('@/assets/signin/field-clear-20px.svg'),
                                                                alt: "Clear",
                                                                onClick: _cache[8] || (_cache[8] = ($event) => (confirmPassword.value = ''))
                                                            }, null, 8, _hoisted_10))
                                                            : _createCommentVNode("", true)
                                                    ]),
                                                    _: 1
                                                }, 8, ["modelValue", "type"]),
                                                (mismatch.value)
                                                    ? (_openBlock(), _createElementBlock("p", _hoisted_11, "Both passwords need to match"))
                                                    : _createCommentVNode("", true)
                                            ], 2)
                                        ]),
                                        _createElementVNode("div", _hoisted_12, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rules.value, (rule) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                    key: rule.label,
                                                    class: "ruleRow"
                                                }, [
                                                    _createElementVNode("div", {
                                                        class: _normalizeClass(["ruleTick", { met: rule.met }])
                                                    }, _cache[21] || (_cache[21] = [
                                                        _createElementVNode("svg", {
                                                            width: "10",
                                                            height: "9",
                                                            viewBox: "0 0 16 13.8819",
                                                            fill: "none",
                                                            xmlns: "http://www.w3.org/2000/svg"
                                                        }, [
                                                            _createElementVNode("path", {
                                                                d: "M1.00002 7.50812L5.16218 12.3514L15 1.00002",
                                                                stroke: "white",
                                                                "stroke-width": "2",
                                                                "stroke-linecap": "round"
                                                            })
                                                        ], -1)
                                                    ]), 2),
                                                    _createElementVNode("span", null, _toDisplayString(rule.label), 1)
                                                ]));
                                            }), 128)),
                                            _cache[22] || (_cache[22] = _createElementVNode("p", { class: "ruleFooter" }, "Save your password somewhere safe.", -1))
                                        ]),
                                        (error.value)
                                            ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(error.value), 1))
                                            : _createCommentVNode("", true),
                                        _createVNode(MGButton, {
                                            class: "ctaButton",
                                            text: "Save new password",
                                            arrow: true,
                                            fullWidth: true,
                                            noCaps: true,
                                            disabled: !canSave.value || busy.value,
                                            onClick: savePassword
                                        }, null, 8, ["disabled"])
                                    ], 64))
                                    : (step.value === 'pin')
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                                            _createElementVNode("div", _hoisted_14, [
                                                _createElementVNode("h1", null, _toDisplayString(pinStep.value === 'set' ? 'Set your PIN' : 'Confirm your PIN'), 1),
                                                _createElementVNode("p", null, _toDisplayString(pinStep.value === 'set'
                                                    ? 'Your password has changed, so choose a 4 digit PIN to sign in super quick next time.'
                                                    : 'Enter your 4-digit PIN again to make sure it matches.'), 1)
                                            ]),
                                            _createElementVNode("div", _hoisted_15, [
                                                _cache[24] || (_cache[24] = _createElementVNode("div", { class: "codeToggle spacer" }, null, -1)),
                                                _createElementVNode("div", _hoisted_16, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(digits.value, (_, i) => {
                                                        return (_openBlock(), _createElementBlock("input", {
                                                            key: i,
                                                            ref_for: true,
                                                            ref: (el) => setBox(el, i),
                                                            class: "codeBox",
                                                            value: boxValue(i),
                                                            type: "text",
                                                            inputmode: "numeric",
                                                            autocomplete: "one-time-code",
                                                            "aria-label": "PIN digit",
                                                            onInput: ($event) => (onDigitInput(i, $event)),
                                                            onKeydown: ($event) => (onDigitKeydown(i, $event))
                                                        }, null, 40, _hoisted_17));
                                                    }), 128))
                                                ]),
                                                _createElementVNode("div", {
                                                    class: "codeToggle",
                                                    onClick: _cache[10] || (_cache[10] = ($event) => (reveal.value = !reveal.value))
                                                }, [
                                                    _createVNode(_component_v_icon, {
                                                        icon: reveal.value ? 'mdi-eye' : 'mdi-eye-off',
                                                        "aria-label": reveal.value ? 'Hide PIN' : 'Show PIN'
                                                    }, null, 8, ["icon", "aria-label"])
                                                ])
                                            ]),
                                            (error.value)
                                                ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(error.value), 1))
                                                : _createCommentVNode("", true),
                                            _createVNode(MGButton, {
                                                class: "ctaButton",
                                                text: pinStep.value === 'set' ? 'Confirm' : 'Confirm PIN',
                                                arrow: true,
                                                fullWidth: true,
                                                noCaps: true,
                                                onClick: submitPin
                                            }, null, 8, ["text"])
                                        ], 64))
                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                                            _cache[25] || (_cache[25] = _createStaticVNode("<svg class=\"doneGraphic\" viewBox=\"-8 -8 192 192\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\" aria-hidden=\"true\" data-v-bdcd130c><circle class=\"sonarWave\" cx=\"88\" cy=\"88\" r=\"40\" fill=\"var(--bright-blue-1)\" data-v-bdcd130c></circle><circle class=\"sonarWave w2\" cx=\"88\" cy=\"88\" r=\"40\" fill=\"var(--bright-blue-1)\" data-v-bdcd130c></circle><circle class=\"sonarWave w3\" cx=\"88\" cy=\"88\" r=\"40\" fill=\"var(--bright-blue-1)\" data-v-bdcd130c></circle><circle cx=\"88\" cy=\"88\" r=\"40\" fill=\"var(--bright-blue-1)\" data-v-bdcd130c></circle><path d=\"M87.4961 64C94.4428 64.0001 99.8272 70.0179 99.8272 77.124V79.4238H101.303C101.829 79.4238 102.345 79.4216 102.778 79.457C103.235 79.4944 103.776 79.5828 104.318 79.8594C104.977 80.195 105.528 80.7054 105.912 81.332L106.066 81.6074L106.161 81.8105C106.364 82.2836 106.436 82.7465 106.469 83.1465C106.504 83.5802 106.502 84.0961 106.502 84.623V102.8C106.502 103.327 106.504 103.843 106.469 104.276C106.431 104.734 106.343 105.274 106.066 105.816C105.683 106.569 105.071 107.181 104.318 107.564C103.776 107.841 103.235 107.928 102.778 107.966C102.345 108.001 101.829 108 101.303 108H73.7002C73.1732 108 72.6574 108.001 72.2236 107.966C71.7665 107.928 71.2264 107.841 70.6836 107.564C69.9311 107.181 69.319 106.569 68.9356 105.816C68.659 105.274 68.5715 104.734 68.5342 104.276C68.4987 103.843 68.5 103.327 68.5 102.8V84.623C68.5 84.0961 68.4987 83.5802 68.5342 83.1465C68.5716 82.6894 68.6591 82.15 68.9356 81.6074C69.319 80.8549 69.9311 80.2429 70.6836 79.8594C71.2264 79.5828 71.7665 79.4944 72.2236 79.457C72.6574 79.4216 73.1732 79.4238 73.7002 79.4238H75.166V77.124C75.166 70.0179 80.5495 64.0001 87.4961 64ZM73.7002 83.4238C73.1073 83.4238 72.782 83.4253 72.5488 83.4443C72.5399 83.4451 72.5314 83.4465 72.5234 83.4473C72.5227 83.4553 72.5212 83.4637 72.5205 83.4727C72.5015 83.7058 72.5 84.0302 72.5 84.623V102.8C72.5 103.393 72.5015 103.718 72.5205 103.951C72.5212 103.96 72.5227 103.968 72.5234 103.976C72.5315 103.976 72.5399 103.979 72.5488 103.979C72.782 103.999 73.1073 104 73.7002 104H101.303C101.895 104 102.22 103.999 102.453 103.979C102.462 103.979 102.47 103.976 102.478 103.976C102.478 103.968 102.481 103.96 102.481 103.951C102.501 103.718 102.502 103.393 102.502 102.8V84.623C102.502 84.0302 102.5 83.7058 102.481 83.4727C102.481 83.4637 102.478 83.4553 102.478 83.4473C102.47 83.4466 102.462 83.445 102.453 83.4443C102.22 83.4253 101.895 83.4238 101.303 83.4238H73.7002ZM88.3516 89.585C89.4559 89.5852 90.3516 90.4806 90.3516 91.585C90.3515 92.2631 90.0124 92.861 89.4961 93.2227V96.0342C89.4961 97.1387 88.6006 98.0341 87.4961 98.0342C86.3916 98.034 85.4961 97.1387 85.4961 96.0342V93.2148C84.9856 92.8525 84.6514 92.2586 84.6514 91.585C84.6514 90.4804 85.5468 89.585 86.6514 89.585H88.3516ZM87.4961 68C83.0321 68.0002 79.166 71.9433 79.166 77.124V79.4238H95.8272V77.124C95.8272 71.9432 91.9601 68.0001 87.4961 68Z\" fill=\"var(--background-color)\" data-v-bdcd130c></path></svg><div class=\"stepHeading done\" data-v-bdcd130c><h1 data-v-bdcd130c>Your password is updated</h1><p data-v-bdcd130c>You&#39;re all set. Sign in using your new password.</p></div>", 2)),
                                            _createVNode(MGButton, {
                                                class: "ctaButton",
                                                text: "Sign in",
                                                arrow: true,
                                                fullWidth: true,
                                                noCaps: true,
                                                onClick: _cache[11] || (_cache[11] = ($event) => (_unref(router).replace({ path: '/signin', query: { skipPin: 'true' } })))
                                            })
                                        ], 64))
                ], 2)
            ], 64));
        };
    }
});
